import { useEffect, useRef } from 'react'
import { useUtilsStore } from './UtilsContext'
import { useLocaleStore } from './LocaleContext'
import { useAdaptiveCardStore } from './AdaptiveCardContext'
import { useViewStore } from './ViewContext'
import { useSpeechStore } from './SpeechContext'
import { useRetorikStore } from './RetorikContext'
import { useActivityStore } from './ActivityContext'
import { InputHintBehaviour, RetorikEvent } from '../../models/enums'
import type { TranslationFile } from '../../models/translationsType'
import type { Choice } from './AdaptiveCardContext'
import type { Configuration } from '../../models/types'
import type { ThemeColors } from '../../models/colorTypes'

interface ExternalHooksHandlerProps {
  instanceId?: string
}

const ExternalHooksHandler = ({ instanceId }: ExternalHooksHandlerProps): null => {
  const askedForKioskParametersOpening = useUtilsStore((state) => state.askedForKioskParametersOpening)
  const unlockClickedButton = useUtilsStore((state) => state.unlockClickedButton)
  const currentSubView = useUtilsStore((state) => state.currentSubView)
  const retorikEvent = useUtilsStore((state) => state.retorikEvent)
  const locale = useLocaleStore((state) => state.locale)
  const isRTL = useLocaleStore((state) => state.isRTL)
  const translation = useLocaleStore((state) => state.currentTranslations)
  const currentValue = useAdaptiveCardStore((state) => state.currentValue)
  const values = useAdaptiveCardStore((state) => state.values)
  const isMobile = useViewStore((state) => state.isMobile)
  const isWidget = useViewStore((state) => state.isWidget)
  const isTactile = useViewStore((state) => state.isTactile)
  const themeColors = useViewStore((state) => state.themeColors)
  const activeRecognitionState = useSpeechStore((state) => state.activeRecognitionState)
  const mode = useSpeechStore((state) => state.mode)
  const muted = useSpeechStore((state) => state.muted)
  const loaderClosed = useRetorikStore((state) => state.loaderClosed)
  const configuration = useRetorikStore((state) => state.configuration)
  const sendBoxRef = useRetorikStore((state) => state.sendBoxRef)
  const conversationId = useActivityStore((state) => state.conversationId)
  const retrievingConversation = useActivityStore((state) => state.retrievingConversation)

  const askedForKioskParametersOpeningRef = useRef<number>(askedForKioskParametersOpening)
  const unlockClickedButtonRef = useRef<boolean>(unlockClickedButton)
  const currentSubViewRef = useRef<number>(currentSubView)
  const retorikEventRef = useRef<RetorikEvent | undefined>(retorikEvent)
  const localeRef = useRef<string>(locale)
  const isRTLRef = useRef<boolean>(isRTL)
  const translationRef = useRef<TranslationFile>(translation)
  const currentValueRef = useRef<Record<string, string | number | boolean | string[] | number[] | Choice> | undefined>(currentValue)
  const valuesRef = useRef<Map<string, string | number | boolean | string[] | number[] | Choice>>(values)
  const isMobileRef = useRef<boolean>(isMobile)
  const isWidgetRef = useRef<boolean>(isWidget)
  const isTactileRef = useRef<boolean>(isTactile)
  const themeColorsRef = useRef<ThemeColors>(themeColors)
  const activeRecognitionStateRef = useRef<number>(activeRecognitionState)
  const modeRef = useRef<number>(mode)
  const mutedRef = useRef<boolean>(muted)
  const loaderClosedRef = useRef<boolean>(loaderClosed)
  const configurationRef = useRef<Configuration>(configuration)
  const isUsedOnBorneRef = useRef<boolean | undefined>(undefined)
  const inputHintBehaviourRef = useRef<InputHintBehaviour | undefined>(undefined)
  const enableDocumentPrintingRef = useRef<boolean | undefined>(false)
  const sendBoxRefRef = useRef<HTMLTextAreaElement | null>(sendBoxRef)
  const conversationIdRef = useRef<string | null>(conversationId)
  const retrievingConversationRef = useRef<boolean>(retrievingConversation)

  const eventDispatch = (name: string, value: any): void => {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          instanceId: instanceId,
          value: value
        }
      })
    )
  }

  useEffect(() => {
    askedForKioskParametersOpeningRef.current = askedForKioskParametersOpening
    eventDispatch('retorik_askedForKioskParametersOpening', askedForKioskParametersOpening)
  }, [askedForKioskParametersOpening])

  useEffect(() => {
    unlockClickedButtonRef.current = unlockClickedButton
    eventDispatch('retorik_unlockClickedButton', unlockClickedButton)
  }, [unlockClickedButton])

  useEffect(() => {
    currentSubViewRef.current = currentSubView
    eventDispatch('retorik_currentSubView', currentSubView)
  }, [currentSubView])

  useEffect(() => {
    retorikEventRef.current = retorikEvent
    eventDispatch('retorik_retorikEvent', retorikEvent)
  }, [retorikEvent])

  useEffect(() => {
    localeRef.current = locale
    eventDispatch('retorik_locale', locale)
  }, [locale])

  useEffect(() => {
    isRTLRef.current = isRTL
    eventDispatch('retorik_isRTL', isRTL)
  }, [isRTL])

  useEffect(() => {
    translationRef.current = translation
    eventDispatch('retorik_translation', translation)
  }, [translation])

  useEffect(() => {
    currentValueRef.current = currentValue
    eventDispatch('retorik_currentValue', currentValue)
  }, [currentValue])

  useEffect(() => {
    valuesRef.current = values
    eventDispatch('retorik_values', values)
  }, [values])

  useEffect(() => {
    isMobileRef.current = isMobile
    eventDispatch('retorik_isMobile', isMobile)
  }, [isMobile])

  useEffect(() => {
    isWidgetRef.current = isWidget
    eventDispatch('retorik_isWidget', isWidget)
  }, [isWidget])

  useEffect(() => {
    isTactileRef.current = isTactile
    eventDispatch('retorik_isTactile', isTactile)
  }, [isTactile])

  useEffect(() => {
    themeColorsRef.current = themeColors
    eventDispatch('retorik_themeColors', themeColors)
  }, [themeColors])

  useEffect(() => {
    activeRecognitionStateRef.current = activeRecognitionState
    eventDispatch('retorik_activeRecognitionState', activeRecognitionState)
  }, [activeRecognitionState])

  useEffect(() => {
    modeRef.current = mode
    eventDispatch('retorik_mode', mode)
  }, [mode])

  useEffect(() => {
    mutedRef.current = muted
    eventDispatch('retorik_muted', muted)
  }, [muted])

  useEffect(() => {
    loaderClosedRef.current = loaderClosed
    eventDispatch('retorik_loaderClosed', loaderClosed)
  }, [loaderClosed])

  useEffect(() => {
    configurationRef.current = configuration
    eventDispatch('retorik_configuration', configuration)
  }, [configuration])

  useEffect(() => {
    sendBoxRefRef.current = sendBoxRef
    eventDispatch('retorik_sendBoxRef', sendBoxRef)
  }, [sendBoxRef])

  useEffect(() => {
    isUsedOnBorneRef.current = configuration.isUsedOnBorne
    eventDispatch('retorik_isUsedOnBorne', configuration.isUsedOnBorne)
  }, [configuration.isUsedOnBorne])

  useEffect(() => {
    inputHintBehaviourRef.current = configuration.speechRecognitionOptions?.inputHintBehaviour
    eventDispatch('retorik_inputHintBehaviour', configuration.speechRecognitionOptions?.inputHintBehaviour)
  }, [configuration.speechRecognitionOptions?.inputHintBehaviour])

  useEffect(() => {
    enableDocumentPrintingRef.current = configuration.enableDocumentPrinting
    eventDispatch('retorik_enableDocumentPrinting', configuration.enableDocumentPrinting)
  }, [configuration.enableDocumentPrinting])

  useEffect(() => {
    conversationIdRef.current = conversationId
    eventDispatch('retorik_conversationId', conversationId)
  }, [conversationId])

  useEffect(() => {
    retrievingConversationRef.current = retrievingConversation
    eventDispatch('retorik_retrievingConversation', retrievingConversation)
  }, [retrievingConversation])

  useEffect(() => {
    document.addEventListener('retorik_call_askedForKioskParametersOpening', () => eventDispatch('retorik_askedForKioskParametersOpening', askedForKioskParametersOpeningRef.current))
    document.addEventListener('retorik_call_unlockClickedButton', () => eventDispatch('retorik_unlockClickedButton', unlockClickedButtonRef.current))
    document.addEventListener('retorik_call_currentSubView', () => eventDispatch('retorik_currentSubView', currentSubViewRef.current))
    document.addEventListener('retorik_call_retorikEvent', () => eventDispatch('retorik_retorikEvent', retorikEventRef.current))
    document.addEventListener('retorik_call_locale', () => eventDispatch('retorik_locale', localeRef.current))
    document.addEventListener('retorik_call_isRTL', () => eventDispatch('retorik_isRTL', isRTLRef.current))
    document.addEventListener('retorik_call_translation', () => eventDispatch('retorik_translation', translationRef.current))
    document.addEventListener('retorik_call_currentValue', () => eventDispatch('retorik_currentValue', currentValueRef.current))
    document.addEventListener('retorik_call_values', () => eventDispatch('retorik_values', valuesRef.current))
    document.addEventListener('retorik_call_isMobile', () => eventDispatch('retorik_isMobile', isMobileRef.current))
    document.addEventListener('retorik_call_isWidget', () => eventDispatch('retorik_isWidget', isWidgetRef.current))
    document.addEventListener('retorik_call_isTactile', () => eventDispatch('retorik_isTactile', isTactileRef.current))
    document.addEventListener('retorik_call_themeColors', () => eventDispatch('retorik_themeColors', themeColorsRef.current))
    document.addEventListener('retorik_call_activeRecognitionState', () => eventDispatch('retorik_activeRecognitionState', activeRecognitionStateRef.current))
    document.addEventListener('retorik_call_mode', () => eventDispatch('retorik_mode', modeRef.current))
    document.addEventListener('retorik_call_muted', () => eventDispatch('retorik_muted', mutedRef.current))
    document.addEventListener('retorik_call_loaderClosed', () => eventDispatch('retorik_loaderClosed', loaderClosedRef.current))
    document.addEventListener('retorik_call_configuration', () => eventDispatch('retorik_configuration', configurationRef.current))
    document.addEventListener('retorik_call_sendBoxRef', () => eventDispatch('retorik_sendBoxRef', sendBoxRefRef.current))
    document.addEventListener('retorik_call_isUsedOnBorne', () => eventDispatch('retorik_isUsedOnBorne', isUsedOnBorneRef.current))
    document.addEventListener('retorik_call_inputHintBehaviour', () => eventDispatch('retorik_inputHintBehaviour', inputHintBehaviourRef.current))
    document.addEventListener('retorik_call_enableDocumentPrinting', () => eventDispatch('retorik_enableDocumentPrinting', enableDocumentPrintingRef.current))
    document.addEventListener('retorik_call_conversationId', () => eventDispatch('retorik_conversationId', conversationIdRef.current))
    document.addEventListener('retorik_call_retrievingConversation', () => eventDispatch('retorik_retrievingConversation', retrievingConversationRef.current))

    return () => {
      document.removeEventListener('retorik_call_askedForKioskParametersOpening', () => eventDispatch('retorik_askedForKioskParametersOpening', askedForKioskParametersOpeningRef.current))
      document.removeEventListener('retorik_call_unlockClickedButton', () => eventDispatch('retorik_unlockClickedButton', unlockClickedButtonRef.current))
      document.removeEventListener('retorik_call_currentSubView', () => eventDispatch('retorik_currentSubView', currentSubViewRef.current))
      document.removeEventListener('retorik_call_retorikEvent', () => eventDispatch('retorik_retorikEvent', retorikEventRef.current))
      document.removeEventListener('retorik_call_locale', () => eventDispatch('retorik_locale', localeRef.current))
      document.removeEventListener('retorik_call_isRTL', () => eventDispatch('retorik_isRTL', isRTLRef.current))
      document.removeEventListener('retorik_call_translation', () => eventDispatch('retorik_translation', translationRef.current))
      document.removeEventListener('retorik_call_currentValue', () => eventDispatch('retorik_currentValue', currentValueRef.current))
      document.removeEventListener('retorik_call_values', () => eventDispatch('retorik_values', valuesRef.current))
      document.removeEventListener('retorik_call_isMobile', () => eventDispatch('retorik_isMobile', isMobileRef.current))
      document.removeEventListener('retorik_call_isWidget', () => eventDispatch('retorik_isWidget', isWidgetRef.current))
      document.removeEventListener('retorik_call_isTactile', () => eventDispatch('retorik_isTactile', isTactileRef.current))
      document.removeEventListener('retorik_call_themeColors', () => eventDispatch('retorik_themeColors', themeColorsRef.current))
      document.removeEventListener('retorik_call_activeRecognitionState', () => eventDispatch('retorik_activeRecognitionState', activeRecognitionStateRef.current))
      document.removeEventListener('retorik_call_mode', () => eventDispatch('retorik_mode', modeRef.current))
      document.removeEventListener('retorik_call_muted', () => eventDispatch('retorik_muted', mutedRef.current))
      document.removeEventListener('retorik_call_loaderClosed', () => eventDispatch('retorik_loaderClosed', loaderClosedRef.current))
      document.removeEventListener('retorik_call_configuration', () => eventDispatch('retorik_configuration', configurationRef.current))
      document.removeEventListener('retorik_call_sendBoxRef', () => eventDispatch('retorik_sendBoxRef', sendBoxRefRef.current))
      document.removeEventListener('retorik_call_isUsedOnBorne', () => eventDispatch('retorik_isUsedOnBorne', isUsedOnBorneRef.current))
      document.removeEventListener('retorik_call_inputHintBehaviour', () => eventDispatch('retorik_inputHintBehaviour', inputHintBehaviourRef.current))
      document.removeEventListener('retorik_call_enableDocumentPrinting', () => eventDispatch('retorik_enableDocumentPrinting', enableDocumentPrintingRef.current))
      document.removeEventListener('retorik_call_conversationId', () => eventDispatch('retorik_conversationId', conversationIdRef.current))
      document.removeEventListener('retorik_call_retrievingConversation', () => eventDispatch('retorik_retrievingConversation', retrievingConversationRef.current))
    }
  }, [])

  return null
}

export default ExternalHooksHandler
